import { useCallback } from 'react';

import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useLazyFetchNotificationsQuery,
  useLazyGetUnreadNotificationCountQuery,
  useLazyMarkAllAsReadQuery,
  useUpdateNotificationMutation,
} from '../api/app-notification.api';
import {
  AppNotificationAC,
  setAppNotificationCurrentPage,
  setUnreadNotificationCount,
} from '../store/app-notification.slice';
import {
  FetchAppNotificationPayload,
  IAppNotification,
  UpdateNotificationPayload,
} from '../types/app-notification.type';

interface UseAppNotification {
  getUnreadNotificationHandler: () => Promise<void>;
  fetchNotificationsHandler: (payload: FetchAppNotificationPayload) => Promise<void>;
  updateNotificationHandler: (payload: UpdateNotificationPayload) => Promise<void>;
  markAllAsReadHandler: () => Promise<boolean>;
  clearNotificationsState: () => void;
  newNotificationItem: (notification: IAppNotification) => void;
}

export const useAppNotification = (): UseAppNotification => {
  const dispatch = useAppDispatch();

  const { items } = useAppSelector((state) => state.appNotification);

  const [getUnreadNotificationQuery] = useLazyGetUnreadNotificationCountQuery();
  const [fetchNotificationsQuery] = useLazyFetchNotificationsQuery();
  const [updateNotificationMutation] = useUpdateNotificationMutation();
  const [markAllAsReadQuery] = useLazyMarkAllAsReadQuery();

  const addNotificationsToStore = useCallback(
    (notifications: IAppNotification[]) => {
      dispatch(AppNotificationAC.setItems([...items, ...notifications]));
    },
    [items],
  );

  const newNotificationItem = (notification: IAppNotification) => {
    dispatch(AppNotificationAC.addOrEditItem(notification));
  };

  const getUnreadNotificationHandler = async () => {
    const { error, data: response } = await getUnreadNotificationQuery();
    const isError = error || !response?.data;
    if (!isError) {
      dispatch(setUnreadNotificationCount(response.data.unread_count));
    }
  };

  const fetchNotificationsHandler = async (payload: FetchAppNotificationPayload) => {
    dispatch(AppNotificationAC.toggleLoading(true));
    const { error, data: response } = await fetchNotificationsQuery(payload);
    dispatch(AppNotificationAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get notifications!');
      return;
    } else {
      batch(() => {
        addNotificationsToStore(response.data);
        dispatch(AppNotificationAC.setPaginationMeta(response.meta));
      });
    }
  };

  const updateNotificationHandler = async (payload: UpdateNotificationPayload) => {
    const { error, data: response } = await updateNotificationMutation(payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to update notification!');
    } else {
      newNotificationItem(response.data);
    }
  };

  const markAllAsReadHandler = async () => {
    dispatch(AppNotificationAC.toggleLoading(true));
    const { error } = await markAllAsReadQuery();
    dispatch(AppNotificationAC.toggleLoading(false));
    const isError = error;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to mark all as read!');
    }
    return !isError;
  };

  const clearNotificationsState = () => {
    batch(() => {
      dispatch(AppNotificationAC.toggleLoading(false));
      dispatch(AppNotificationAC.clearItems());
      dispatch(AppNotificationAC.setPaginationMeta(null));
      dispatch(setAppNotificationCurrentPage(1));
    });
  };

  return {
    getUnreadNotificationHandler,
    fetchNotificationsHandler,
    updateNotificationHandler,
    markAllAsReadHandler,
    clearNotificationsState,
    newNotificationItem,
  };
};
